// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { addSpanEvent } from '@imprivata-cloud/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRuleSets, getRules, updateRules } from '@api/portalServices';
import type { RuleResource, RuleSetItem } from '@api/types';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { AuthnLevel } from '@portal/containers/security/constants';
import { decode } from './codec/codec';
import type { Decoded, OpaqueRule, Rule } from './types';

// `encode` patches the resource the rules were read from, so it travels with its decoding.
export type RuleSet = { resource: RuleResource; decoded: Decoded };

// Identifiers travel as attributes, never in the event name, so a served value cannot forge an event.
const traceOpaqueRules = (instanceKey: string, opaque: OpaqueRule[]): void => {
  for (const { served, servedIndex } of opaque) {
    addSpanEvent('conditions.opaque-rule', {
      instanceKey,
      ruleId: served.ruleId,
      exprType: served.exprType,
      reason: served.capabilities?.system === true ? 'system-rule-unrecognised' : 'expression-unrecognised',
      servedIndex,
    });
  }
};

// BFF workaround: the rules service serves `allowedValues: []`. Remove once it serves the vocabulary.
const withActionVocabulary = (rule: Rule): Rule =>
  rule.allowedValues.length > 0 ? rule : { ...rule, allowedValues: Object.values(AuthnLevel) };

export const useRulesQuery = (instanceKey: string, enabled: boolean) =>
  useQuery<RuleSet, ApiError>({
    queryKey: [QueryKey.AUTHN_RULES, instanceKey],
    queryFn: async () => {
      const { rules } = await getRules({ instanceKey });
      const resource = rules.find(item => item.instanceKey === instanceKey);
      if (resource === undefined) { throw new Error(`no rule set served for instanceKey ${instanceKey}`); }

      const decoded = decode(resource);
      traceOpaqueRules(instanceKey, decoded.opaque);
      return { resource, decoded: { ...decoded, rules: decoded.rules.map(withActionVocabulary) } };
    },
    enabled,
    retry: false,
    staleTime: 0,
  });

export const useRulesMutation = (instanceKey: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, RuleResource>({
    mutationFn: resource =>
      updateRules({
        instanceKey: resource.instanceKey,
        version: resource.version,
        valueRule: resource.valueRule,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.AUTHN_RULES, instanceKey] });
    },
    retry: false,
  });
};

export const useRuleSets = (enabled: boolean) =>
  useQuery<RuleSetItem[], ApiError>({
    queryKey: [QueryKey.RULE_SETS],
    queryFn: async () => (await getRuleSets()).ruleSets,
    enabled,
    retry: false,
    staleTime: 0,
  });
